import { useState, useEffect, useCallback } from 'react';
import { CalendarIcon, CreditCardIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { getSupabaseClient } from '../lib/supabase';

type AccountStatus = {
  connected: boolean;
  accountId?: string;
  status?: {
    fully_setup: boolean;
    charges_enabled: boolean;
    payouts_enabled: boolean;
    details_submitted: boolean;
  };
  requirements?: {
    has_requirements: boolean;
    has_past_due: boolean;
    currently_due: string[];
    past_due: string[];
    eventually_due: string[];
    pending_verification: string[];
    disabled_reason: string | null;
  };
  error?: string;
  message?: string;
};

type BalanceData = {
  available_to_withdraw_cents: number;
  currency: string;
  payout_schedule: string | null;
};

interface SettingsPageProps {
  initialTab?: 'calendar' | 'payments';
}

export default function SettingsPage({ initialTab = 'calendar' }: SettingsPageProps) {
  const [activeTab, setActiveTab] = useState<'calendar' | 'payments'>(initialTab);

  // Update active tab when initialTab prop changes (e.g., from URL hash)
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [lastStatusCheck, setLastStatusCheck] = useState<Date | null>(null);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [outlookCalendarConnected, setOutlookCalendarConnected] = useState(false);
  const [calendarStatusLoading, setCalendarStatusLoading] = useState(true);
  const [balanceData, setBalanceData] = useState<BalanceData | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState<string | null>(null);

  // Update URL hash when tab changes
  const handleTabChange = (tab: 'calendar' | 'payments') => {
    setActiveTab(tab);
    // Update the hash to include the sub-tab for settings
    window.history.replaceState(null, '', `#settings-${tab}`);
  };

  const calendarIntegrations = [
    { name: 'Google Calendar', connected: googleCalendarConnected },
    { name: 'Outlook Calendar', connected: outlookCalendarConnected },
  ];

  const fetchCalendarStatus = useCallback(async () => {
    try {
      setCalendarStatusLoading(true);
      const supabase = getSupabaseClient();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        console.error('Not authenticated');
        return;
      }

      // Check if user has a connected Google Calendar
      const { data, error } = await supabase
        .from('guide_calendars')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('provider', 'google')
        .limit(1)
        .maybeSingle();

      if (!error && data) {
        setGoogleCalendarConnected(true);
      }

      // Check if user has a connected Microsoft Outlook Calendar
      const { data: msData, error: msError } = await supabase
        .from('user_identities')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('provider', 'microsoft')
        .limit(1)
        .maybeSingle();

      if (!msError && msData) {
        setOutlookCalendarConnected(true);
      }
    } catch (error) {
      console.error('Error fetching calendar status:', error);
    } finally {
      setCalendarStatusLoading(false);
    }
  }, []);

  const fetchBalance = useCallback(async () => {
    try {
      setBalanceLoading(true);
      const supabase = getSupabaseClient();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        console.error('Not authenticated');
        return;
      }

      const env = (window as any).__ENV__;
      const baseUrl = env?.SUPABASE_URL;

      if (!baseUrl) {
        console.error('Configuration error');
        return;
      }

      const response = await fetch(`${baseUrl}/functions/v1/connect-balance`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch balance');
        return;
      }

      const data = await response.json();
      setBalanceData(data);
    } catch (error) {
      console.error('Error fetching balance:', error);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  const fetchAccountStatus = useCallback(async () => {
    try {
      setStatusLoading(true);
      const supabase = getSupabaseClient();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        console.error('Not authenticated');
        return;
      }

      const env = (window as any).__ENV__;
      const baseUrl = env?.SUPABASE_URL;

      if (!baseUrl) {
        console.error('Configuration error');
        return;
      }

      const response = await fetch(`${baseUrl}/functions/v1/connect-status`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch account status');
        return;
      }

      const data = await response.json();
      setAccountStatus(data);
      setLastStatusCheck(new Date());

      // Fetch balance if fully setup
      if (data.connected && data.status?.fully_setup) {
        fetchBalance();
      }
    } catch (error) {
      console.error('Error fetching account status:', error);
    } finally {
      setStatusLoading(false);
    }
  }, [fetchBalance]);

  const handleWithdraw = async () => {
    if (!balanceData || balanceData.available_to_withdraw_cents === 0) {
      return;
    }

    if (!confirm(`Withdraw $${(balanceData.available_to_withdraw_cents / 100).toFixed(2)} to your bank account?`)) {
      return;
    }

    try {
      setWithdrawLoading(true);
      setStripeError(null);
      setWithdrawSuccess(null);

      const supabase = getSupabaseClient();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('You must be logged in to withdraw funds');
      }

      const env = (window as any).__ENV__;
      const baseUrl = env?.SUPABASE_URL;

      if (!baseUrl) {
        throw new Error('Configuration error. Please refresh and try again.');
      }

      const response = await fetch(`${baseUrl}/functions/v1/connect-withdraw`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}), // Withdraw full balance
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process withdrawal');
      }

      const result = await response.json();

      // Optimistically update balance
      setBalanceData(prev => prev ? {
        ...prev,
        available_to_withdraw_cents: 0,
      } : null);

      setWithdrawSuccess(`Withdrawal of $${(result.amount_cents / 100).toFixed(2)} initiated successfully!`);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setWithdrawSuccess(null);
      }, 5000);

      // Refresh balance after a short delay
      setTimeout(() => {
        fetchBalance();
      }, 2000);
    } catch (error) {
      console.error('Error withdrawing funds:', error);
      setStripeError(error instanceof Error ? error.message : 'Failed to withdraw funds');
    } finally {
      setWithdrawLoading(false);
    }
  };

  // Fetch calendar status on mount
  useEffect(() => {
    fetchCalendarStatus();
  }, [fetchCalendarStatus]);

  // Handle OAuth callback URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const calendarConnected = params.get('calendar_connected');
    const calendarError = params.get('calendar_error');
    const oauthSuccess = params.get('oauth_success');
    const oauthError = params.get('oauth_error');
    const oauthErrorDescription = params.get('oauth_error_description');

    if (calendarConnected === 'true' || oauthSuccess === 'microsoft') {
      // Refresh calendar status
      fetchCalendarStatus();
      // Clear URL params
      const currentPath = window.location.pathname;
      window.history.replaceState({}, '', currentPath);
    }

    if (calendarError) {
      setStripeError(decodeURIComponent(calendarError));
      // Clear URL params
      const currentPath = window.location.pathname;
      window.history.replaceState({}, '', currentPath);
    }

    if (oauthError) {
      const errorMessage = oauthErrorDescription
        ? decodeURIComponent(oauthErrorDescription)
        : 'OAuth authentication failed';
      setStripeError(errorMessage);
      // Clear URL params
      const currentPath = window.location.pathname;
      window.history.replaceState({}, '', currentPath);
    }
  }, [fetchCalendarStatus]);

  // Fetch status on mount and when switching to payments tab
  useEffect(() => {
    if (activeTab === 'payments') {
      fetchAccountStatus();
    }
  }, [activeTab, fetchAccountStatus]);

  // Poll for status updates every 30 seconds when on payments tab
  useEffect(() => {
    if (activeTab !== 'payments') return;

    const interval = setInterval(() => {
      fetchAccountStatus();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [activeTab, fetchAccountStatus]);

  const handleSetupIntegration = async (integrationName: string, isConnected: boolean) => {
    if (integrationName !== 'Google Calendar' && integrationName !== 'Outlook Calendar') {
      console.log(`${integrationName} integration not yet implemented`);
      return;
    }

    // Handle Microsoft Outlook
    if (integrationName === 'Outlook Calendar') {
      // If already connected, disconnect
      if (isConnected) {
        if (!confirm('Are you sure you want to disconnect your Outlook Calendar?')) {
          return;
        }

        try {
          setStripeLoading(true);
          setStripeError(null);

          const supabase = getSupabaseClient();
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();

          if (sessionError || !session) {
            throw new Error('You must be logged in to disconnect calendar');
          }

          // Delete the user identity
          const { error } = await supabase
            .from('user_identities')
            .delete()
            .eq('user_id', session.user.id)
            .eq('provider', 'microsoft');

          if (error) {
            throw error;
          }

          setOutlookCalendarConnected(false);
          setStripeError(null);
        } catch (error) {
          console.error('Error disconnecting Outlook Calendar:', error);
          setStripeError(error instanceof Error ? error.message : 'Failed to disconnect Outlook Calendar');
        } finally {
          setStripeLoading(false);
        }
        return;
      }

      // If not connected, start Microsoft OAuth flow
      try {
        setStripeLoading(true);
        setStripeError(null);

        const supabase = getSupabaseClient();
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          throw new Error('You must be logged in to set up calendar integration');
        }

        const env = (window as any).__ENV__;
        const baseUrl = env?.SUPABASE_URL;

        if (!baseUrl) {
          throw new Error('Configuration error. Please refresh and try again.');
        }

        const response = await fetch(`${baseUrl}/functions/v1/microsoft-oauth-start`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            return_to: '/settings',
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to start Outlook Calendar setup');
        }

        const { authorization_url } = await response.json();

        if (!authorization_url) {
          throw new Error('No authorization URL received');
        }

        // Redirect to Microsoft OAuth
        window.location.href = authorization_url;
      } catch (error) {
        console.error('Error setting up Outlook Calendar:', error);
        setStripeError(error instanceof Error ? error.message : 'Failed to set up Outlook Calendar');
        setStripeLoading(false);
      }
      return;
    }

    // Handle Google Calendar
    // If already connected, disconnect instead
    if (isConnected) {
      if (!confirm('Are you sure you want to disconnect your Google Calendar?')) {
        return;
      }

      try {
        setStripeLoading(true);
        setStripeError(null);

        const supabase = getSupabaseClient();
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          throw new Error('You must be logged in to disconnect calendar');
        }

        // Delete the calendar connection
        const { error } = await supabase
          .from('guide_calendars')
          .delete()
          .eq('user_id', session.user.id)
          .eq('provider', 'google');

        if (error) {
          throw error;
        }

        setGoogleCalendarConnected(false);
        setStripeError(null);
      } catch (error) {
        console.error('Error disconnecting Google Calendar:', error);
        setStripeError(error instanceof Error ? error.message : 'Failed to disconnect Google Calendar');
      } finally {
        setStripeLoading(false);
      }
      return;
    }

    // If not connected, start OAuth flow
    try {
      setStripeLoading(true);
      setStripeError(null);

      const supabase = getSupabaseClient();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('You must be logged in to set up calendar integration');
      }

      const env = (window as any).__ENV__;
      const baseUrl = env?.SUPABASE_URL;

      if (!baseUrl) {
        throw new Error('Configuration error. Please refresh and try again.');
      }

      const response = await fetch(`${baseUrl}/functions/v1/google-calendar-auth-start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start Google Calendar setup');
      }

      const { url } = await response.json();

      if (!url) {
        throw new Error('No authorization URL received');
      }

      // Redirect to Google OAuth
      window.location.href = url;
    } catch (error) {
      console.error('Error setting up Google Calendar:', error);
      setStripeError(error instanceof Error ? error.message : 'Failed to set up Google Calendar');
      setStripeLoading(false);
    }
  };

  const handleSetupStripeConnect = async () => {
    try {
      setStripeLoading(true);
      setStripeError(null);

      const supabase = getSupabaseClient();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('You must be logged in to set up payments');
      }

      const env = (window as any).__ENV__;
      const baseUrl = env?.SUPABASE_URL;

      if (!baseUrl) {
        throw new Error('Configuration error. Please refresh and try again.');
      }

      const response = await fetch(`${baseUrl}/functions/v1/connect-start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start Stripe Connect setup');
      }

      const { url } = await response.json();

      if (!url) {
        throw new Error('No onboarding URL received');
      }

      // Redirect to Stripe Connect onboarding
      window.location.href = url;
    } catch (error) {
      console.error('Error setting up Stripe Connect:', error);
      setStripeError(error instanceof Error ? error.message : 'Failed to set up Stripe Connect');
      setStripeLoading(false);
    }
  };

  const formatRequirement = (req: string): string => {
    // Convert snake_case to Title Case
    return req
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Page Header */}
        <div className="rounded-2xl border border-[#2C2731] bg-[#2C2731] p-6 text-[#FFFCEF] shadow-sm">
          <h1 className="text-2xl font-semibold">Settings</h1>
          <p className="mt-2 text-sm text-[#FFFCEF]/80">
            Configure your calendar integrations and payment settings
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-[#2C2731] pb-2">
          <button
            onClick={() => handleTabChange('calendar')}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === 'calendar'
                ? 'bg-[#2C2731] text-[#FFFCEF]'
                : 'text-[#2C2731] hover:bg-[#E8DFBD]'
            }`}
          >
            <CalendarIcon className="h-5 w-5" />
            <span>Calendar</span>
          </button>
          <button
            onClick={() => handleTabChange('payments')}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === 'payments'
                ? 'bg-[#2C2731] text-[#FFFCEF]'
                : 'text-[#2C2731] hover:bg-[#E8DFBD]'
            }`}
          >
            <CreditCardIcon className="h-5 w-5" />
            <span>Payments</span>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'calendar' && (
          <section className="rounded-xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-sm">
            <div className="mb-6">
              <h2 className="text-lg font-medium text-[#2C2731]">Calendar Integrations</h2>
              <p className="text-sm text-[#2C2731]/70">
                Connect your calendar to automatically sync your availability and appointments
              </p>
            </div>

            <div className="space-y-4">
              {calendarIntegrations.map((integration) => (
                <div
                  key={integration.name}
                  className="flex items-center justify-between p-4 rounded-lg border border-[#2C2731] bg-[#FFFCEF] hover:bg-[#E8DFBD] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#2C2731] flex items-center justify-center">
                      <CalendarIcon className="h-5 w-5 text-[#FFFCEF]" />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#2C2731]">{integration.name}</h3>
                      <p className="text-sm text-[#2C2731]/70">
                        {integration.connected ? 'Connected' : 'Not connected'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleSetupIntegration(integration.name, integration.connected)}
                    disabled={stripeLoading && (integration.name === 'Google Calendar' || integration.name === 'Outlook Calendar')}
                    className={`px-4 py-2 rounded-md text-[#FFFCEF] transition-colors text-sm font-medium ${
                      stripeLoading && (integration.name === 'Google Calendar' || integration.name === 'Outlook Calendar')
                        ? 'bg-[#2C2731]/40 cursor-not-allowed'
                        : integration.connected
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-[#2C2731] hover:bg-[#241F2A]'
                    }`}
                  >
                    {stripeLoading && (integration.name === 'Google Calendar' || integration.name === 'Outlook Calendar')
                      ? (integration.connected ? 'Disconnecting...' : 'Connecting...')
                      : (integration.connected ? 'Disconnect' : 'Set Up')}
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {activeTab === 'payments' && (
          <section className="rounded-xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-sm">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-[#2C2731]">Payment Settings</h2>
                <p className="text-sm text-[#2C2731]/70">
                  Connect your Stripe account to receive payments from seekers
                </p>
              </div>
              {lastStatusCheck && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={fetchAccountStatus}
                    disabled={statusLoading}
                    className="p-2 rounded-md hover:bg-[#E8DFBD] transition-colors"
                    title="Refresh status"
                  >
                    <ArrowPathIcon className={`h-5 w-5 text-[#2C2731] ${statusLoading ? 'animate-spin' : ''}`} />
                  </button>
                  <span className="text-xs text-[#2C2731]/60">
                    Updated {new Date(lastStatusCheck).toLocaleTimeString()}
                  </span>
                </div>
              )}
            </div>

            {stripeError && (
              <div className="mb-6 p-4 rounded-lg border border-red-300 bg-red-100">
                <p className="text-sm text-red-800">{stripeError}</p>
              </div>
            )}

            {statusLoading && !accountStatus ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C2731] mx-auto mb-2"></div>
                <p className="text-sm text-[#2C2731]/70">Loading account status...</p>
              </div>
            ) : !accountStatus?.connected ? (
              // Not connected state
              <>
                <div className="p-6 rounded-lg border border-[#2C2731] bg-[#E8DFBD]">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-[#2C2731] flex items-center justify-center flex-shrink-0">
                      <CreditCardIcon className="h-6 w-6 text-[#FFFCEF]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-[#2C2731] mb-2">Stripe Connect</h3>
                      <p className="text-sm text-[#2C2731]/70 mb-4">
                        Connect your Stripe account to start accepting payments. Stripe handles all payment
                        processing securely and deposits funds directly to your bank account.
                      </p>
                      <button
                        onClick={handleSetupStripeConnect}
                        disabled={stripeLoading}
                        className={`px-5 py-2.5 rounded-md text-[#FFFCEF] transition-colors text-sm font-medium ${
                          stripeLoading
                            ? 'bg-[#2C2731]/40 cursor-not-allowed'
                            : 'bg-[#2C2731] hover:bg-[#241F2A]'
                        }`}
                      >
                        {stripeLoading ? 'Connecting...' : 'Set Up Stripe Connect'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mt-6 p-4 rounded-lg bg-[#FFFCEF] border border-[#2C2731]/20">
                  <p className="text-xs text-[#2C2731]/70">
                    <strong>Note:</strong> You'll need a Stripe account to receive payments. If you don't have
                    one, you'll be guided through creating an account during the setup process.
                  </p>
                </div>
              </>
            ) : accountStatus.status?.fully_setup ? (
              // Fully setup state
              <>
                <div className="p-6 rounded-lg border border-green-600 bg-green-50">
                  <div className="flex items-start gap-4">
                    <CheckCircleIcon className="h-12 w-12 text-green-600 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-green-900 mb-2">Payments Fully Configured</h3>
                      <p className="text-sm text-green-800 mb-4">
                        Your Stripe Connect account is active and ready to receive payments from seekers.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 text-sm text-green-800">
                          <CheckCircleIcon className="h-4 w-4" />
                          <span>Charges enabled</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-green-800">
                          <CheckCircleIcon className="h-4 w-4" />
                          <span>Payouts enabled</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Withdraw Balance Section */}
                <div className="mt-6 p-6 rounded-lg border border-[#2C2731] bg-[#FFFCEF]">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-[#2C2731] flex items-center justify-center">
                        <BanknotesIcon className="h-5 w-5 text-[#FFFCEF]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-[#2C2731]">Available Balance</h3>
                        <p className="text-sm text-[#2C2731]/70">Withdraw funds to your bank account</p>
                      </div>
                    </div>
                    {balanceLoading && (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2C2731]"></div>
                    )}
                  </div>

                  {withdrawSuccess && (
                    <div className="mb-4 p-3 rounded-lg border border-green-300 bg-green-100">
                      <p className="text-sm text-green-800">{withdrawSuccess}</p>
                    </div>
                  )}

                  {balanceData && (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-3xl font-bold text-[#2C2731]">
                          ${(balanceData.available_to_withdraw_cents / 100).toFixed(2)}
                        </p>
                        <p className="text-xs text-[#2C2731]/60 mt-1">
                          {balanceData.payout_schedule === 'manual' ? 'Manual payouts' : 'Automatic payouts'}
                        </p>
                      </div>
                      <button
                        onClick={handleWithdraw}
                        disabled={withdrawLoading || balanceData.available_to_withdraw_cents === 0}
                        className={`px-5 py-2.5 rounded-md text-[#FFFCEF] transition-colors text-sm font-medium ${
                          withdrawLoading || balanceData.available_to_withdraw_cents === 0
                            ? 'bg-[#2C2731]/40 cursor-not-allowed'
                            : 'bg-[#2C2731] hover:bg-[#241F2A]'
                        }`}
                      >
                        {withdrawLoading ? 'Processing...' : 'Withdraw'}
                      </button>
                    </div>
                  )}

                  {!balanceData && !balanceLoading && (
                    <p className="text-sm text-[#2C2731]/70">Unable to load balance. Please refresh the page.</p>
                  )}
                </div>
              </>
            ) : (
              // Incomplete setup state with requirements
              <>
                <div className="p-6 rounded-lg border border-orange-400 bg-orange-50 mb-4">
                  <div className="flex items-start gap-4">
                    <ExclamationTriangleIcon className="h-12 w-12 text-orange-600 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-orange-900 mb-2">Action Required</h3>
                      <p className="text-sm text-orange-800 mb-4">
                        Your Stripe Connect account needs additional information to complete setup.
                      </p>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="flex items-center gap-2 text-sm">
                          {accountStatus.status?.charges_enabled ? (
                            <>
                              <CheckCircleIcon className="h-4 w-4 text-green-600" />
                              <span className="text-green-800">Charges enabled</span>
                            </>
                          ) : (
                            <>
                              <ExclamationTriangleIcon className="h-4 w-4 text-orange-600" />
                              <span className="text-orange-800">Charges pending</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          {accountStatus.status?.payouts_enabled ? (
                            <>
                              <CheckCircleIcon className="h-4 w-4 text-green-600" />
                              <span className="text-green-800">Payouts enabled</span>
                            </>
                          ) : (
                            <>
                              <ExclamationTriangleIcon className="h-4 w-4 text-orange-600" />
                              <span className="text-orange-800">Payouts pending</span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={handleSetupStripeConnect}
                        disabled={stripeLoading}
                        className={`px-5 py-2.5 rounded-md text-white transition-colors text-sm font-medium ${
                          stripeLoading
                            ? 'bg-orange-400 cursor-not-allowed'
                            : 'bg-orange-600 hover:bg-orange-700'
                        }`}
                      >
                        {stripeLoading ? 'Loading...' : 'Complete Setup'}
                      </button>
                    </div>
                  </div>
                </div>

                {accountStatus.requirements && (accountStatus.requirements.currently_due.length > 0 || accountStatus.requirements.past_due.length > 0) && (
                  <div className="p-6 rounded-lg border border-[#2C2731] bg-[#FFFCEF]">
                    <h4 className="text-sm font-medium text-[#2C2731] mb-3">Required Information</h4>
                    <div className="space-y-2">
                      {accountStatus.requirements.past_due.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-red-600 mb-1">Past Due:</p>
                          <ul className="space-y-1 ml-4">
                            {accountStatus.requirements.past_due.map((req) => (
                              <li key={req} className="text-sm text-red-800 list-disc">
                                {formatRequirement(req)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {accountStatus.requirements.currently_due.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-orange-600 mb-1">Currently Due:</p>
                          <ul className="space-y-1 ml-4">
                            {accountStatus.requirements.currently_due.map((req) => (
                              <li key={req} className="text-sm text-orange-800 list-disc">
                                {formatRequirement(req)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
